import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  FormSection,
  actions,
  formValueSelector,
  getFormSyncErrors,
  getFormValues,
} from 'redux-form'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Card } from 'ggx-componentlibrary/components/card/card.component'
import {
  Heading,
  SmallText,
} from 'ggx-componentlibrary/design/typography/typography.styles'
import { SPACING } from 'ggx-componentlibrary/design/spacing/spacing.constants'
import { AddressForm } from './address-form/address-form.component'
import { addressBookService } from 'ggx-src/address-book/api/api.service'
import {
  AlignItemsCenter,
  ClearRemoveButton,
  VerticalCenterAlign,
  StyledWaypoint,
} from './pickup-dropoff-form.component.styles'
import { DELIVERY_FORM_NAME } from '../order-form.component'
import IconAddContact from 'ggx-src/van-booking/order/sidebar/order-form/order-info/route-form/route-waypoints/assets/icon-add-contact.svg'
import IconAddedContact from 'ggx-src/van-booking/order/sidebar/order-form/order-info/route-form/route-waypoints/assets/icon-added-contact.svg'
import { apiService as deliveryService } from 'ggx-src/delivery/api/api.service'
import uniqBy from 'lodash/uniqBy'
import { displayAlert } from 'ggx-componentlibrary/components/alerts/alerts.component'
import { AddAddressModal } from 'ggx-src/address-book/add-address-modal/add-address-modal.component'
import pickBy from 'lodash/pickBy'
import IconBin from './assets/icon-bin.svg'

const CardHeading = styled(Heading)`
  margin-bottom: ${SPACING.M};
`

function useCheckWaypointState(name) {
  const waypoint =
    useSelector(state => formValueSelector(DELIVERY_FORM_NAME)(state, name)) ??
    {}
  const [isValueInAddressBook, setIsValueInAddressBook] = useState(false)
  useEffect(() => {
    addressBookService.getList().then(list => {
      setIsValueInAddressBook(
        list.some(
          item =>
            (item.street_address || '') === (waypoint.address || '') &&
            (item.address_detail || '') === (waypoint.details || '') &&
            (item.contact_name || '') === (waypoint.name || '') &&
            (item.phone_number || '') === (waypoint.phone_number || '') &&
            item.geo_location.includes(waypoint.latitude) &&
            item.geo_location.includes(waypoint.longitude)
        )
      )
    })
  }, [
    waypoint.saved_address_id,
    waypoint.address,
    waypoint.details,
    waypoint.latitude,
    waypoint.longitude,
    waypoint.name,
    waypoint.phone_number,
  ])

  const formSyncErrors = useSelector(state =>
    getFormSyncErrors(DELIVERY_FORM_NAME)(state)
  )

  return [
    isValueInAddressBook,
    !formSyncErrors[name].address &&
      !formSyncErrors[name].latitude &&
      !formSyncErrors[name].longitude,
  ]
}

const PickupDropoffForm = ({ dataTestid, handleResetAddressSyncErrors }) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const [
    isShipperValueInAddressBook,
    isShipperAddressValid,
  ] = useCheckWaypointState('shipper')
  const [
    isRecipientValueInAddressBook,
    isRecipientAddressValid,
  ] = useCheckWaypointState('recipient')

  const [showAddDialogForWaypoint, setShowAddDialogForWaypoint] = useState(null)
  const formValues = useSelector(state =>
    getFormValues(DELIVERY_FORM_NAME)(state)
  )
  const [recentOptions, setRecentOptions] = useState([])
  useEffect(() => {
    deliveryService
      .getContacts()
      .catch(() => ({ contacts: [] }))
      .then(result => {
        const options = result.contacts.map(item => ({
          value: {
            address: item.address,
            lat: Number(item.latitude),
            lon: Number(item.longitude),
          },
          groupType: 'recent-address',
        }))
        setRecentOptions(
          uniqBy(options, ({ value }) => `${value.lat}-${value.lon}`)
        )
      })
  }, [])
  const addAddress = async value => {
    try {
      const newAddress = await addressBookService.addAddress(value)
      displayAlert({ message: t('address_saved') })
      dispatch(
        actions.change(DELIVERY_FORM_NAME, showAddDialogForWaypoint, {
          address: '',
          name: '',
          phone_number: '',
        })
      )
      dispatch(
        actions.change(DELIVERY_FORM_NAME, showAddDialogForWaypoint, {
          saved_address_id: newAddress.id,
          address: value.address,
          latitude: value.lat.toString(),
          longitude: value.lon.toString(),
          details: value.detail_address,
          name: value.contact_name,
          phone_number: value.contact_phone_number,
        })
      )
      setShowAddDialogForWaypoint(null)
    } catch (error) {
      if (error?.response?.data?.i18n_key === 'duplicate_address') {
        displayAlert({
          type: 'error',
          message: t('error__street_address_saved'),
        })
      }
      console.error(error)
    }
  }

  const setWaypoint = (name, value) => {
    const isLocationAllSetOrNot =
      Boolean(value.address) !== Boolean(value.latitude) &&
      Boolean(value.latitude) !== Boolean(value.longitude)
    if (isLocationAllSetOrNot)
      throw new Error('Address, latitude, and longitude must be all set or not')

    dispatch(
      actions.change(DELIVERY_FORM_NAME, name, {
        ...(formValues[name] ?? {}),
        ...pickBy(value, Boolean),
      })
    )
  }

  return (
    <Card data-testid={dataTestid}>
      <CardHeading>
        {t('orders.new.orderDetails.title.pickupDropoff')}
      </CardHeading>
      <StyledWaypoint
        caption={
          <VerticalCenterAlign>
            {t('orders.new.basicInfo.subtitle.pickup')}
            <ClearRemoveButton
              type="button"
              onClick={() =>
                dispatch(actions.change(DELIVERY_FORM_NAME, 'shipper', {}))
              }
            >
              <IconBin />
            </ClearRemoveButton>
          </VerticalCenterAlign>
        }
        iconRight={
          isShipperAddressValid ? (
            isShipperValueInAddressBook ? (
              <AlignItemsCenter>
                <IconAddedContact />
                <SmallText typographyStyles="mb-none c-grey_medium">
                  {t('in_address_book')}
                </SmallText>
              </AlignItemsCenter>
            ) : (
              <AlignItemsCenter
                onClick={() => setShowAddDialogForWaypoint('shipper')}
              >
                <IconAddContact />
                <SmallText typographyStyles="mb-none c-blue_medium">
                  {t('add_to_address_book')}
                </SmallText>
              </AlignItemsCenter>
            )
          ) : null
        }
      >
        <FormSection name="shipper">
          <AddressForm
            isAddressBookOptionsReady={!showAddDialogForWaypoint}
            onClickAddAddress={() => setShowAddDialogForWaypoint('shipper')}
            handleResetAddressSyncErrors={handleResetAddressSyncErrors}
            section="shipper"
            onWaypointChange={value => setWaypoint('shipper', value)}
          />
        </FormSection>
      </StyledWaypoint>
      <StyledWaypoint
        isDestination
        caption={
          <VerticalCenterAlign>
            {t('orders.new.basicInfo.subtitle.dropoff')}
            <ClearRemoveButton
              type="button"
              onClick={() =>
                dispatch(actions.change(DELIVERY_FORM_NAME, 'recipient', {}))
              }
            >
              <IconBin />
            </ClearRemoveButton>
          </VerticalCenterAlign>
        }
        iconRight={
          isRecipientAddressValid ? (
            isRecipientValueInAddressBook ? (
              <AlignItemsCenter>
                <IconAddedContact />
                <SmallText typographyStyles="mb-none c-grey_medium">
                  {t('in_address_book')}
                </SmallText>
              </AlignItemsCenter>
            ) : (
              <AlignItemsCenter
                onClick={() => setShowAddDialogForWaypoint('recipient')}
              >
                <IconAddContact />
                <SmallText typographyStyles="mb-none c-blue_medium">
                  {t('add_to_address_book')}
                </SmallText>
              </AlignItemsCenter>
            )
          ) : null
        }
      >
        <FormSection name="recipient">
          <AddressForm
            isAddressBookOptionsReady={!showAddDialogForWaypoint}
            onClickAddAddress={() => setShowAddDialogForWaypoint('recipient')}
            handleResetAddressSyncErrors={handleResetAddressSyncErrors}
            section="recipient"
            onWaypointChange={value => setWaypoint('recipient', value)}
          />
        </FormSection>
      </StyledWaypoint>

      {typeof showAddDialogForWaypoint === 'string' ? (
        <AddAddressModal
          isPlacingOrder={true}
          recentOptions={recentOptions}
          defaultValues={{
            address: formValues[showAddDialogForWaypoint]?.address,
            lat: Number(formValues[showAddDialogForWaypoint]?.latitude),
            lon: Number(formValues[showAddDialogForWaypoint]?.longitude),
            detail_address: formValues[showAddDialogForWaypoint]?.details,
            contact_name: formValues[showAddDialogForWaypoint]?.name,
            contact_phone_number:
              formValues[showAddDialogForWaypoint]?.phone_number,
          }}
          onSubmit={addAddress}
          onClose={() => setShowAddDialogForWaypoint(null)}
        />
      ) : null}
    </Card>
  )
}

PickupDropoffForm.propTypes = {
  dataTestid: PropTypes.string,
}

export { PickupDropoffForm }
